import { Component } from '@theme/component';

const DISMISSED_KEY = 'be-geo-banner';
const COUNTRY_CACHE_KEY = 'be-geo-country';
const DAY_MS = 86400000;

/**
 * The geolocation banner: a toast pinned to the top of every page
 * (sections/geo-banner.liquid, rendered from layout/theme.liquid) that points
 * visitors detected outside the home country to the international store.
 *
 * Detection uses Shopify's own `browsing_context_suggestions` storefront
 * endpoint (the same IP-based lookup that powers market recommendations), so
 * no third-party geo service is involved. The detected country is cached in
 * sessionStorage to avoid re-fetching on every page; dismissing the banner —
 * or following its link — is remembered in localStorage for the section's
 * configured number of days. Detection or storage failures simply leave the
 * banner hidden.
 *
 * In the theme editor the banner never shows by itself; the merchant previews
 * it via section select (see the design-mode script in the section).
 *
 * @typedef {object} GeoBannerRefs
 * @property {HTMLDivElement} banner - The banner bar element.
 *
 * @extends {Component<GeoBannerRefs>}
 */
class GeoBannerComponent extends Component {
  requiredRefs = ['banner'];

  connectedCallback() {
    super.connectedCallback();

    // Testing escape hatch: `?geo-banner` in the URL forces the banner to
    // show, bypassing both the country check and any earlier dismissal —
    // detection can't be previewed from inside the home country otherwise.
    if (new URLSearchParams(window.location.search).has('geo-banner')) {
      this.show();
      return;
    }

    if ('designMode' in this.dataset) return;
    if (this.#isDismissed()) return;

    this.#detect();
  }

  /**
   * Shows the banner. Also called by the design-mode preview script.
   */
  show() {
    this.refs.banner.hidden = false;
  }

  /**
   * Hides the banner. Also called by the design-mode preview script.
   */
  hide() {
    this.refs.banner.hidden = true;
  }

  /**
   * Hides the banner and remembers the dismissal.
   */
  dismiss() {
    this.remember();
    this.hide();
  }

  /**
   * Remembers the dismissal for the configured number of days. Bound to the
   * international-store link too, so visitors who follow it aren't nagged
   * again when they come back.
   */
  remember() {
    const days = Number(this.dataset.rememberDays) || 30;

    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + days * DAY_MS));
    } catch {
      // Storage blocked: the visitor may see the banner again on other pages.
    }
  }

  /**
   * Shows the banner if the visitor's detected country differs from the
   * section's home country.
   */
  async #detect() {
    const country = await this.#detectedCountry();
    const homeCountry = (this.dataset.homeCountry || 'NZ').toUpperCase();

    if (!country || country === homeCountry) return;

    this.show();
  }

  /**
   * @returns {Promise<string | null>} The visitor's detected ISO country code.
   */
  async #detectedCountry() {
    try {
      const cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
      if (cached) return cached;
    } catch {
      // Storage blocked: fall through to a fresh lookup.
    }

    try {
      const root = this.dataset.rootUrl || '/';
      const separator = root.endsWith('/') ? '' : '/';
      const response = await fetch(
        `${root}${separator}browsing_context_suggestions.json?country%5Benabled%5D=true`
      );

      if (!response.ok) return null;

      const data = await response.json();
      const handle = data?.detected_values?.country?.handle;

      if (typeof handle !== 'string' || handle === '') return null;

      const country = handle.toUpperCase();

      try {
        sessionStorage.setItem(COUNTRY_CACHE_KEY, country);
      } catch {
        // Storage blocked: the next page repeats the lookup.
      }

      return country;
    } catch {
      return null;
    }
  }

  /**
   * @returns {boolean} Whether a previous dismissal is still valid.
   */
  #isDismissed() {
    try {
      return Number(localStorage.getItem(DISMISSED_KEY)) > Date.now();
    } catch {
      return false;
    }
  }
}

if (!customElements.get('be-geo-banner-component')) {
  customElements.define('be-geo-banner-component', GeoBannerComponent);
}
